/**
 * Decisions for the live inbox connection (PLAN.md §8), kept pure so they can be tested with plain
 * numbers: when to show "Reconnecting…", when to poll instead, and how to batch notifications.
 */

/** Show the badge (and start polling) once the channels have been down this long. */
export const RECONNECTING_AFTER_MS = 10_000;
/** While reconnecting, refetch the inbox this often so nothing goes stale silently. */
export const POLL_EVERY_MS = 10_000;
/** Notifications that arrive close together cause one refetch. */
export const INVALIDATE_DEBOUNCE_MS = 150;

export interface ConnectionClock {
  /** When the channels stopped being joined, or null while they are. */
  downSince: number | null;
  /** When the fallback poll last ran. */
  lastPollAt: number;
}

export function isReconnecting(state: ConnectionClock, now: number): boolean {
  return state.downSince !== null && now - state.downSince >= RECONNECTING_AFTER_MS;
}

export function shouldPoll(state: ConnectionClock, now: number): boolean {
  return isReconnecting(state, now) && now - state.lastPollAt >= POLL_EVERY_MS;
}

/**
 * Collects merchant ids from notifications and flushes them together after `delayMs` (one refetch for
 * a burst). `flush` receives the ids seen since the last flush.
 */
export function createBatcher(
  delayMs: number,
  flush: (merchantIds: Set<string>) => void,
  timers: {
    set: (fn: () => void, ms: number) => unknown;
    clear: (handle: unknown) => void;
  } = {
    set: (fn, ms) => setTimeout(fn, ms),
    clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
  },
) {
  let pending = new Set<string>();
  let handle: unknown = null;
  return {
    add(merchantId: string) {
      pending.add(merchantId);
      handle ??= timers.set(() => {
        handle = null;
        const ids = pending;
        pending = new Set();
        flush(ids);
      }, delayMs);
    },
    cancel() {
      if (handle !== null) timers.clear(handle);
      handle = null;
      pending = new Set();
    },
  };
}
